import { FiatIcon } from 'BreetAssets';
import { capitalizeFirstLetter, capitalizeFirstLetters, type IDropDownItem, showToast } from 'BreetHelpers';
import { useCryptoSettings, useModalApi } from 'BreetHooks';
import { selectBusiness, selectFiatMeta, selectTargetEnvironment, useAppSelector } from 'BreetRedux';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import type { IFundRequestContext } from '../DialogFundRequest.helpers';
import { FundRequestContext } from './FormContext';

const FUNDING_METHOD_SESSION_KEY = 'breet.fundRequest.fundingMethod';

type StoredFundingMethod = 'crypto' | 'bank';

const buildFundingMethodKey = (businessId: string | undefined, environment: string | undefined): string =>
	`${FUNDING_METHOD_SESSION_KEY}:${businessId ?? 'none'}:${environment ?? 'none'}`;

const readStoredFundingMethod = (key: string): StoredFundingMethod | null => {
	try {
		const value = sessionStorage.getItem(key);
		return value === 'crypto' || value === 'bank' ? value : null;
	} catch {
		return null;
	}
};

const storeFundingMethod = (key: string, value: StoredFundingMethod): void => {
	try {
		sessionStorage.setItem(key, value);
	} catch {
		/* sessionStorage unavailable (private mode / SSR) — remembering is best-effort. */
	}
};

export const FundRequestProvider = ({ children }: { children?: ReactNode }) => {
	const { openModalDirect } = useModalApi();

	const { currentReceiveCurrency } = useCryptoSettings();

	const fiatMeta = useAppSelector(selectFiatMeta);
	const business = useAppSelector(selectBusiness);
	const targetEnvironment = useAppSelector(selectTargetEnvironment);

	const fundingMethodKey = useMemo(
		() => buildFundingMethodKey(business?.id, targetEnvironment),
		[business?.id, targetEnvironment]
	);

	const [selectedWallet, setSelectedWallet] = useState<IDropDownItem | undefined>();
	const [fundingMethod, setFundingMethod] = useState<string>('');

	const selectWallet = useCallback((wallet?: IDropDownItem) => {
		setSelectedWallet(wallet);
		setFundingMethod('');
	}, []);

	useEffect(() => {
		if (selectedWallet || !currentReceiveCurrency) return;

		const defaultWallet: IDropDownItem =
			currentReceiveCurrency === 'usd'
				? {
						code: 'USD Wallet',
						name: 'USD Wallet',
						value: 'USD',
						icon: (
							<FiatIcon
								currencyCode='USD'
								variant='round'
							/>
						),
					}
				: {
						code: `${capitalizeFirstLetters(fiatMeta.currencyShort)} Wallet`,
						name: `${capitalizeFirstLetters(fiatMeta.currencyShort)} Wallet`,
						value: fiatMeta.currency,
						icon: (
							<FiatIcon
								currencyCode={fiatMeta.currency}
								variant='round'
							/>
						),
					};

		setSelectedWallet(defaultWallet);
	}, [currentReceiveCurrency, fiatMeta, selectedWallet]);

	const applyFundingMethod = useCallback(
		(item: IDropDownItem) => {
			if (!currentReceiveCurrency) {
				showToast('error', 'Please select a receive currency first');
				return;
			}

			let fundingMethodArg: string | undefined;

			if (selectedWallet?.value === 'USD' && item.value === 'Crypto' && currentReceiveCurrency !== 'usd') {
				fundingMethodArg = 'crypto';
			}

			if (selectedWallet?.value !== 'USD' && item.value === 'Crypto' && currentReceiveCurrency === 'usd') {
				fundingMethodArg = 'fiat';
			}

			if (fundingMethodArg) {
				openModalDirect({ id: 'settlementWallet', metadata: { fundingMethod: fundingMethodArg } });
				return;
			}

			if (selectedWallet?.value !== 'USD') {
				storeFundingMethod(fundingMethodKey, item.value === 'Crypto' ? 'crypto' : 'bank');
			}

			setFundingMethod(item.value);
		},
		[currentReceiveCurrency, openModalDirect, selectedWallet?.value, fundingMethodKey]
	);

	const selectFundingMethod = useCallback(
		(item: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			applyFundingMethod(item);
		},
		[applyFundingMethod]
	);

	useEffect(() => {
		if (selectedWallet?.value === 'USD' && !fundingMethod) {
			applyFundingMethod({ name: 'Crypto', value: 'Crypto', code: '', icon: '' });
		}
	}, [selectedWallet?.value, fundingMethod, applyFundingMethod]);

	useEffect(() => {
		if (!selectedWallet || selectedWallet.value === 'USD' || fundingMethod) return;

		const stored = readStoredFundingMethod(fundingMethodKey);
		if (!stored) return;

		const restoredValue = stored === 'crypto' ? 'Crypto' : `${capitalizeFirstLetter(fiatMeta.currencyShort)} Bank Transfer`;

		applyFundingMethod({ name: restoredValue, value: restoredValue, code: '', icon: '' });
	}, [selectedWallet, fundingMethod, fiatMeta, fundingMethodKey, applyFundingMethod]);

	const providerValue = useMemo(
		(): IFundRequestContext => ({
			selectedWallet,
			selectFundingMethod,
			selectWallet,
			fundingMethod,
		}),
		[selectedWallet, selectFundingMethod, selectWallet, fundingMethod]
	);

	return <FundRequestContext.Provider value={providerValue}>{children}</FundRequestContext.Provider>;
};
